import { supabase } from '../lib/supabase';

export class PushNotificationService {
  private static readonly PUBLIC_VAPID_KEY = 'BJ-Sg9ppJ5uLLoXH9lYYvXqKvGYrKLDFgw6xMBqwQhU6Ij7pxQbA8IVOnhPNs8eX1y1yPO_QYVmgs59wKxuXe-s';

  static async requestPermission() {
    try {
      if (!('Notification' in window)) {
        console.warn('Ce navigateur ne supporte pas les notifications push');
        return false;
      }

      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.error('Erreur lors de la demande de permission:', error);
      return false;
    }
  }

  static async subscribeToPush() {
    try {
      // Vérifier si on est déjà abonné
      const registration = await navigator.serviceWorker.ready;
      const existingSubscription = await registration.pushManager.getSubscription();
      
      // Si on a déjà une souscription, on la réutilise
      if (existingSubscription) {
        console.log('Utilisation d\'une souscription existante');
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await this.saveSubscription(user.id, existingSubscription);
        }
        return existingSubscription;
      }
      
      // Sinon, on crée une nouvelle souscription
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.PUBLIC_VAPID_KEY)
      });

      // Sauvegarder la subscription dans la table notifications
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await this.saveSubscription(user.id, subscription);
      }

      return subscription;
    } catch (error) {
      console.error('Erreur lors de l\'inscription aux notifications:', error);
      return null;
    }
  }

  private static async saveSubscription(userId: string, subscription: PushSubscription) {
    try {
      // Vérifier si une subscription existe déjà
      const { data: existingSubscriptions } = await supabase
        .from('notifications')
        .select('id')
        .eq('user_id', userId)
        .eq('title', 'Notification Subscription')
        .eq('message', 'Subscription aux notifications push');
        
      // Ne pas insérer de nouvelle subscription si une existe déjà
      if (existingSubscriptions && existingSubscriptions.length > 0) {
        console.log('Une subscription aux notifications existe déjà pour cet utilisateur');
        return;
      }
      
      const { error } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          title: 'Notification Subscription',
          message: 'Subscription aux notifications push',
          subscription_data: JSON.stringify(subscription),
          read: true
        });

      if (error) {
        console.error('Erreur lors de la sauvegarde de la subscription:', error);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la subscription:', error);
    }
  }

  static async notifyUser(userId: string, title: string, message: string, type: string) {
    try {
      const { error } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          title,
          message,
          translation_key: type,
          read: false
        });

      if (error) {
        console.error('Erreur lors de l\'envoi de la notification:', error);
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la notification:', error);
    }
  }

  // Méthodes spécifiques utilisant notifyUser
  static async notifyRelationCertified(userId: string, relationName: string) {
    await this.notifyUser(
      userId,
      'Relation certifiée',
      `${relationName} a certifié votre relation.`,
      'relation_certified'
    );
  }

  static async notifyRelationRejected(userId: string, relationName: string) {
    await this.notifyUser(
      userId,
      'Relation rejetée',
      `${relationName} a rejeté votre relation.`,
      'relation_rejected'
    );
  }

  static async notifyRelationDeleted(userId: string, relationName: string) {
    await this.notifyUser(
      userId,
      'Relation supprimée',
      `${relationName} a supprimé votre relation.`,
      'relation_deleted'
    );
  }

  static async notifyRelationSearched(_userId: string, _relationName: string) {
    // Ne rien faire ici car Home.tsx envoie déjà une notification similaire
    // avec la clé 'relationSearchedByUser'
    console.log('Notification skipped to avoid duplicates with Home.tsx notification');
    // Ne pas appeler this.notifyUser pour éviter le doublon
    return;
  }

  private static urlBase64ToUint8Array(base64String: string) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
} 